'use client';

import { cn } from '@/lib/utils';
import { FC } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { StackedBarChartLegend } from './StackedBarChartLegend';
import { ChartDataItem, ChartSeriesItem } from './types';
import { useStackedBarChart } from './useStackedBarChart';
import { getBarPosition, getBarRadiusByPosition } from './utils';

export type StackedBarChartProps = {
  data: ChartDataItem[];
  series: (ChartSeriesItem & { percentage?: number })[];
  legendSeries?: (ChartSeriesItem & { percentage?: number })[];
  chartId?: string;
  xKey?: string;
  xLabels?: string[];
  useWeekdayLabels?: boolean;
  valueScale?: number;
  height?: number | string;
  barSize?: number;
  barCategoryGap?: number | string;
  className?: string;
  chartClassName?: string;
  legendClassName?: string;
  legendContainerClassName?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  valueFormatter?: (value: number, name?: string) => string;
  xTickFormatter?: (value: string | number) => string;
  t?: (key: string) => string;
};

const StackedBarChart: FC<StackedBarChartProps> = ({
  data = [],
  series = [],
  legendSeries,
  chartId = 'stack',
  xKey = 'day',
  xLabels,
  useWeekdayLabels,
  valueScale = 1,
  height = 500,
  barSize = 6,
  barCategoryGap,
  className,
  chartClassName,
  legendClassName,
  legendContainerClassName,
  showLegend = true,
  showGrid = true,
  valueFormatter,
  xTickFormatter,
  t,
}) => {
  const { chartData, hasData, legendItems, niceMax, dynamicTicks } = useStackedBarChart({
    data,
    series,
    legendSeries,
    xKey,
    xLabels,
    useWeekdayLabels,
    valueScale,
    t,
  });

  const resolvedHeight = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={cn('flex flex-col lg:flex-row items-stretch justify-between gap-6 w-full h-full', className)}
      style={resolvedHeight ? { height: resolvedHeight } : undefined}
    >
      <div className={cn('relative flex-1 min-w-0 h-full', chartClassName)}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            barCategoryGap={barCategoryGap}
          >
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-subtle)" opacity={0.6} />
            )}
            <XAxis
              className="text-xs font-medium"
              tickLine={false}
              axisLine={false}
              dataKey={xKey}
              interval={0}
              tickMargin={8}
              tick={{ fill: 'var(--contrast)' }}
              tickFormatter={xTickFormatter}
            />
            <YAxis
              className="text-xs font-medium"
              tickLine={false}
              axisLine={false}
              tick={{ fill: 'var(--contrast)' }}
              ticks={dynamicTicks}
              domain={[0, niceMax]}
              tickFormatter={(v: number) => String(v)}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                valueFormatter ? valueFormatter(value, name) : `¥${(value * valueScale).toLocaleString()}`,
                series.find((s) => s.name === name || s.key === name)?.name ?? name,
              ]}
              cursor={{ fill: 'transparent' }}
            />
            {series.map((seriesItem, index) => {
              const position = getBarPosition(index, series.length);
              return (
                <Bar
                  key={seriesItem.key}
                  dataKey={seriesItem.key}
                  barSize={barSize}
                  stackId={chartId}
                  fill={seriesItem.color}
                  name={seriesItem.name}
                  hide={!hasData}
                  isAnimationActive={false}
                  radius={getBarRadiusByPosition[position] ?? 0}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showLegend && legendItems.length > 0 && (
        <div
          className={cn(
            'w-full lg:w-44 xl:w-48 shrink-0 flex flex-col justify-center self-center',
            legendContainerClassName,
          )}
        >
          <StackedBarChartLegend items={legendItems} className={legendClassName} />
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
export { StackedBarChartLegend } from './StackedBarChartLegend';
export type { ChartDataItem, ChartLegendItem, ChartSeriesItem, StackChartSeries } from './types';
export { useStackedBarChart } from './useStackedBarChart';
